import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioField, CustomSelectField, TextField } from 'ui/shared/controls';
import { BuildingId } from 'shared/models';
import { ErrorMessage } from 'ui/shared/messages';
import { useBuilding } from 'hooks/useBuilding/useBuilding';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';

require('./CopyThemeForm.css');

export type Props = {
    submitting: boolean;
    errorMessage?: string;
    buildingId: BuildingId;
    initialDisplayName: string;
    onSearch(searchText: string, searchResultsCallback: (options: Record<string, unknown>) => void): void;
    onSubmit(data: CopyThemeProps): void;
    onCancel(): void;
};

export type CopyMode = 'copy' | 'share';

export type CopyThemeProps = {
    selectedBuildingId: BuildingId;
    selectedBuildingUrlSlug?: string;
    mode: CopyMode;
    displayName: string;
    destination: string;
};


export const CopyThemeForm = ({ submitting, buildingId, errorMessage, initialDisplayName, onSearch, onSubmit, onCancel }: Props) => {
    const { t } = useTranslation();

    const [ selectedBuildingId, setSelectedBuildingId ] = useState(buildingId);
    const [ destination, setDestination ] = useState('this');
    const [ displayName, setDisplayName ] = useState(initialDisplayName || '');
    const [ mode, setMode ] = useState<CopyMode>('copy');
    const { building: selectedBuilding } = useBuilding(selectedBuildingId);
    const selectedBuildingUrlSlug = selectedBuilding?.urlSlug;

    const handleDisplayNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setDisplayName(e.currentTarget.value);
    }, [ setDisplayName ]);

    const handleModeChange = useCallback((e: FormEvent<HTMLInputElement>) => {
        if (e.currentTarget.checked) {
            setMode(e.currentTarget.value as CopyMode);
        }
    }, [ setMode ]);

    const handleDestinationChange = useCallback((e: FormEvent<HTMLInputElement>) => {
        if (e.currentTarget.checked) {
            setDestination(e.currentTarget.value);
        }
    }, [ setDestination ]);

    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();
        onSubmit({ selectedBuildingId, selectedBuildingUrlSlug, mode, displayName, destination });
    }, [ selectedBuildingId, selectedBuildingUrlSlug, mode, displayName, destination, onSubmit ]);

    // Documentation of apiSettings is here https://semantic-ui.com/behaviors/api.html#/settings
    const apiSettings = useMemo(() => ({
        responseAsync: (settings: any, searchResultsCallback: (options: Record<string, unknown>) => void) => {
            const { urlData: { query: searchText } } = settings;
            onSearch(searchText, searchResultsCallback);
        },
    }), [ onSearch ]);

    return (
        <div id="copy-theme-form-wrapper">
            <form
                id="copy-theme-form"
                onSubmit={ handleSubmit }
                className={ `ui form ${submitting ? 'loading-form' : 'normal-form'}` }
            >
                <div className="grid">
                    <div className="row">
                        <div className="column four-wide-column">
                            { t('THEMES.COPY_DIALOG.LABELS.METHOD') }
                        </div>
                        <div className="column four-wide-column">
                            <RadioField
                                id={ "copy-theme-mode-copy" }
                                label={ t("THEMES.COPY_DIALOG.LABELS.COPY") }
                                myValue={ "copy" }
                                toolTipKey={ "THEMES.COPY_DIALOG.TOOL_TIP.COPY_MSG" }
                                input={ {
                                    value: mode,
                                    name: 'mode',
                                    onChange: handleModeChange,
                                } }
                            />
                        </div>
                        <div className="column four-wide-column">
                            <RadioField
                                id={ "copy-theme-mode-share" }
                                label={ t("THEMES.COPY_DIALOG.LABELS.SHARE") }
                                myValue={ "share" }
                                toolTipKey={ "THEMES.COPY_DIALOG.TOOL_TIP.SHARE_MSG" }
                                input={ {
                                    value: mode,
                                    name: 'mode',
                                    onChange: handleModeChange,
                                } }
                            />
                        </div>
                    </div>
                    { mode === 'copy' &&
                        <div className="row">
                            <div className="column four-wide-column theme-name-title">
                                { t('THEMES.COPY_DIALOG.LABELS.THEME_NAME') }
                            </div>
                            <div className="column twelve-wide-column">
                                <TextField
                                    name="displayName"
                                    type="text"
                                    id="copy-theme-method-name"
                                    maxLength={ 32 }
                                    value={ displayName }
                                    className="text-field"
                                    onChange={ handleDisplayNameChange }
                                    readOnly={ mode !== 'copy' }
                                    placeholder={ t("THEMES.TITLE_PLACEHOLDER") }
                                />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="column four-wide-column">
                            { t('THEMES.COPY_DIALOG.LABELS.DESTINATION') }
                        </div>
                        <div className="column twelve-wide-column">
                            {mode === 'copy' &&
                                <div>
                                    <RadioField
                                        id={ "copy-theme-dest-this" }
                                        labelKey={ "THEMES.COPY_DIALOG.LABELS.THIS_BUILDING" }
                                        myValue={ "this" }
                                        input={ {
                                            value: destination,
                                            name: 'destination',
                                            onChange: handleDestinationChange,
                                        } }
                                    />
                                </div>
                            }
                            <div>
                                <RadioField
                                    id={ "copy-theme-dest-other" }
                                    labelKey={ "THEMES.COPY_DIALOG.LABELS.ANOTHER_BUILDING" }
                                    myValue={ "other" }
                                    input={ {
                                        value: destination,
                                        name: 'destination',
                                        onChange: handleDestinationChange,
                                    } }
                                />
                            </div>
                            { destination !== 'this' && (
                                <div className="building-select-wrapper">
                                    <CustomSelectField
                                        id="copy-theme-building"
                                        promptKey="THEMES.COPY_DIALOG.SELECT_A_BUILDING"
                                        required={ true }
                                        value={ selectedBuildingId || '' }
                                        onChange={ setSelectedBuildingId }
                                        apiSettings={ apiSettings }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                { submitting &&
                    <LoadingSpinner size="small" />
                }

                <div className="button-container">
                    <button
                        type="submit"
                        disabled={ submitting }
                        className="ui button primary"
                    >
                        { t(`COMMAND.${mode === 'copy' ? 'COPY' : 'SHARE'}`) }
                    </button>
                    <button
                        type="button"
                        className="ui button"
                        disabled={ submitting }
                        onClick={ onCancel }
                    >
                        { t('COMMAND.CANCEL') }
                    </button>
                </div>
                <ErrorMessage message={ errorMessage }/>
            </form>
        </div>
    );
};